<template>
  <SelectboxBase
    v-model:show="showOption"
    v-model:selected="itemSelected"
    :type="type"
    :tabindex="tabindex"
    :round="round"
    :hideArrow="hideArrow"
    v-ClickOutside="closeSelect"
  >
    <template v-if="text != null" #text>
      <span class="gray-dark mr-4 mb-dn">{{ text }}</span>
    </template>
    <template #select>
      <template v-if="type === 'search' || type === 'ratings'">
        <input
          type="search"
          class="custom-search bd-n fs-14 lh-17 outline-0 hand bg-transparent db wp-100 hp-100 absolute left-0 top-0 pr-16 fw-400"
          :class="noBackground ? 'pl-16 no-bg' : 'pl-40'"
          :placeholder="placeholder"
          v-model="searchQuery"
        />
      </template>
      <template v-else>
        <template v-if="noPlaceholder">
          <div :class="[(optionSelected?.name || optionSelected?.name == 0) ? 'black' : '']" >
            {{(optionSelected?.name || optionSelected?.name == 0) ? optionSelected?.name : placeholder}}
          </div>
        </template>
        <template v-else>
          <div v-html="placeholder"></div>
        </template>
      </template>
    </template>
    <template #clearSelection>
      <div
        v-if="type === 'ratings' && Object.keys(optionSelected).length"
        class="mr-12 fs-0 relative z-2"
        @click="clearSelection($event)"
      >
        <svg
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 1L1 7M7 7L1 1"
            stroke="#5C5C5C"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </template>
    <template #select-option>
      <div v-if="searchQuery && (type === 'search' || type === 'ratings')">
        <div v-if="searchedOptions.length">
          <div
            v-for="(item, index) in searchedOptions"
            :key="index"
            class="custom-option-item"
            @click="onChangeOption(item)"
          >
            <div v-html="highlightText(item.name)"></div>
          </div>
        </div>
        <div v-else class="fs-12 lh-16 pt-8 pb-8 pl-16 pr-16 fw-600">
          No results
        </div>
      </div>
      <div v-else>
        <div
          v-for="(item, index) in options"
          :key="index"
          class="custom-option-item"
          @click="onChangeOption(item)"
        >
          <span>{{ item.name }}</span>
          <label class="fr db" v-if="type === 'ratings' && item.rating">
            <span class="yellow fs-16 mr-6">&starf;</span>
            <span class="fs-14 black">{{ getRating(item.rating) }}</span>
          </label>
        </div>
      </div>
    </template>
  </SelectboxBase>
</template>

<script>
import SelectboxBase from './SelectboxBase'
import { onMounted, inject, ref, watch, computed } from 'vue'
import ClickOutside from '~/directives/ClickOutside'

export default {
  name: 'SelectBox',
  directives: { ClickOutside },
  components: { SelectboxBase },
  emits: ['update:optionSelected', 'update:placeholder', 'trigger', 'deselect'],
  props: {
    type: {
      type: String,
      default: 'text',
    },
    text: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    options: {
      type: [Array, Object, String],
      default: null,
    },
    optionSelected: {
      type: [Array, Object, String, Number],
      default: null,
    },
    tabindex: {
      type: Number,
      default: 0,
    },
    round: {
      type: Boolean,
      default: false,
    },
    noPlaceholder: {
      type: Boolean,
      default: false,
    },
    status: {
      type: Boolean,
      default: false,
    },
    hideArrow: {
      type: Boolean,
      default: false
    },
    noBackground: {
      type: Boolean,
      default: false
    },
  },
  setup(props, { emit }) {
    const showOption = ref(false)
    const itemSelected = ref(false)
    const optionSelectedBg = inject('optionSelectedBg', false)
    const searchQuery = ref('')

    const onChangeOption = (item) => {
      searchQuery.value = item.name
      if (optionSelectedBg) itemSelected.value = true
      showOption.value = false
      emit('update:optionSelected', item)
      emit('trigger')
    }

    const getRating = (item) => {
      return JSON.parse(item).toFixed(1)
    }

    const highlightText = (text) => {
      let q = searchQuery.value.trim()
      return text.replace(new RegExp(q, 'gi'), (match) => {
        return '<span class="highlighted">' + match + '</span>'
      })
    }

    const searchedOptions = computed(() => {
      return props.options.filter((option) => {
        return (
          option.name.toLowerCase().indexOf(searchQuery.value.toLowerCase()) !=
          -1
        )
      })
    })

    const clearSelection = (e) => {
      searchQuery.value = ''
      e.stopPropagation()
      itemSelected.value = false
      emit('deselect', '')
      emit('update:optionSelected', {})
    }

    const closeSelect = () => {
      showOption.value = false
    }

    watch(
      () => props.optionSelected,
      (value) => {
        searchQuery.value = value.name
      },
    )

    onMounted(() => {
      if (props.status === true && optionSelectedBg) itemSelected.value = true
    })

    return {
      getRating,
      showOption,
      onChangeOption,
      itemSelected,
      optionSelectedBg,
      clearSelection,
      searchQuery,
      searchedOptions,
      highlightText,
      closeSelect
    }
  },
}
</script>

<style lang="scss" scoped>
.custom-search {
  background-image: url('https://s3-cdn.designerjourneys.com/static/icons/icon-search.svg');
  background-position: 16px;
  background-repeat: no-repeat;
  &.no-bg {
    background: none;
  }
}
</style>
